import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { Link, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DeleteInsurancePolicyProductModal } from 'src/components/insurance-policy/delete/DeleteInsurancePolicyProductModal'
import { InsurancePolicyProductFormModal } from 'src/components/insurance-policy/product-form/InsurancePolicyProductFormModal'
import type {
  InsurancePolicyProductFormMode,
  InsurancePolicyProductFormValues,
} from 'src/components/insurance-policy/product-form/InsurancePolicyProductForm.interface'
import {
  toCreateInsurancePolicyProductPayload,
  toUpdateInsurancePolicyProductPayload,
} from 'src/components/insurance-policy/product-form/InsurancePolicyProductForm.mapper'
import { InsurancePolicyProductsTable } from 'src/components/insurance-policy/products-table/InsurancePolicyProductsTable'
import { Button } from 'src/components/shared/Button'
import {
  AdminCreateInsurancePolicyProduct,
  AdminDeleteInsurancePolicyProduct,
  AdminFetchDocusignTemplates,
  AdminFetchInsurancePolicies,
  AdminFetchInsuranceProducts,
  AdminUpdateInsurancePolicyProduct,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { buildCustomerTabPath } from 'src/pages/admin-dashboard/adminDashboardNav'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import { getDocusignTemplates } from 'src/selectors/docusignTemplate'
import { getInsuranceProducts } from 'src/selectors/insuranceProduct'
import {
  getInsurancePolicies,
  getInsurancePoliciesError,
  getInsurancePoliciesLoadedPartnerId,
  getInsurancePolicyProductDeletingId,
  getInsurancePolicySaving,
} from 'src/selectors/insurancePolicy'
import {
  findPolicyProduct,
  selectableInsuranceProducts,
  withResolvedInsuranceProducts,
} from 'src/utils/insurancePolicyList'
import { GSIcon } from 'src/components/shared/GSIcon'

const INSURANCE_TAB_SLUG = 'insurance'

interface ProductFormModalState {
  mode: InsurancePolicyProductFormMode
  /** The GLOBAL product id, not the row id — it survives an immutable revision. */
  insuranceProductId: string | null
}

const CustomerInsurancePolicyPageComponent = () => {
  const { customerId, insurancePolicyPermanentId } = useParams<{
    customerId: string
    insurancePolicyPermanentId: string
  }>()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  // Bumped by Refresh and by every successful mutation — included in the fetch effect's
  // dependency array below so any of them re-triggers the requests.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [productFormModalState, setProductFormModalState] = useState<ProductFormModalState | null>(
    null,
  )
  const [productPendingDelete, setProductPendingDelete] = useState<InsurancePolicyProduct | null>(
    null,
  )

  const { isAuthenticated } = useAuthSession()
  const policies = useSelector(getInsurancePolicies)
  const loadedPartnerId = useSelector(getInsurancePoliciesLoadedPartnerId)
  const policiesError = useSelector(getInsurancePoliciesError)
  const isSaving = useSelector(getInsurancePolicySaving)
  const deletingProductId = useSelector(getInsurancePolicyProductDeletingId)
  const insuranceProductCatalog = useSelector(getInsuranceProducts)
  const docusignTemplates = useSelector(getDocusignTemplates)
  // Add, Edit and Remove product all call `insurance_policies_manage` endpoints.
  const canManagePolicies = useHasPermission(InternalPermission.InsurancePoliciesManage)
  // Cross-family reads (spec §6): skipped without their permission. The picker then has no
  // products (Add stays disabled by the existing empty-picker rule) and the Contract column and
  // select resolve no titles.
  const canViewInsuranceProducts = useHasPermission(InternalPermission.InsuranceProductsView)
  const canViewDocusignTemplates = useHasPermission(InternalPermission.DocusignTemplatesView)

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  /**
   * Three fetches in one effect: the policies (this page is derived from the list, not fetched
   * by id — a deep link or a refresh lands here with nothing loaded yet), the global
   * insurance-product catalog that the add-product picker offers, and this customer's DocuSign
   * templates that the Contract select and the Contract column resolve.
   */
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchInsurancePolicies.request({ partnerId: customerId }))

    if (canViewInsuranceProducts) {
      asyncDispatchWithErrorToast(AdminFetchInsuranceProducts.request())
    }

    if (canViewDocusignTemplates) {
      asyncDispatchWithErrorToast(AdminFetchDocusignTemplates.request({ partnerId: customerId }))
    }
  }, [
    asyncDispatchWithErrorToast,
    isAuthenticated,
    customerId,
    canViewInsuranceProducts,
    canViewDocusignTemplates,
    refreshToken,
  ])

  /**
   * Derived, never stored. Deriving by `permanentId` is what survives an immutable mutation:
   * the refetched list carries a new `id` and new product row ids for the same `permanentId`,
   * and this page follows it without noticing. A policy that is gone — deleted, or archived out
   * from under the admin — resolves to `null`, which renders the "not found" state below rather
   * than crashing.
   */
  const policy = useMemo(() => {
    const found = policies.find((candidate) => {
      return candidate.permanentId === insurancePolicyPermanentId
    })

    return found ?? null
  }, [policies, insurancePolicyPermanentId])

  // The list endpoint's nested `insuranceProduct` is stripped by a backend DTO bug (ENG-750) —
  // backfill it from the catalog this page already fetches. Every downstream read of a
  // product's name goes through this derived policy instead of `policy` directly.
  const resolvedPolicy = useMemo(() => {
    return withResolvedInsuranceProducts(policy, insuranceProductCatalog)
  }, [policy, insuranceProductCatalog])

  const selectableProducts = useMemo(() => {
    return selectableInsuranceProducts(insuranceProductCatalog, resolvedPolicy)
  }, [insuranceProductCatalog, resolvedPolicy])

  const editingPolicyProduct = useMemo(() => {
    if (!productFormModalState || productFormModalState.mode === 'create') {
      return null
    }

    return findPolicyProduct(resolvedPolicy, productFormModalState.insuranceProductId)
  }, [productFormModalState, resolvedPolicy])

  const isDeletingPendingProduct =
    productPendingDelete !== null && deletingProductId === productPendingDelete.insuranceProductId

  const handleOpenAddProduct = useCallback(() => {
    setProductFormModalState({ mode: 'create', insuranceProductId: null })
  }, [])

  const handleOpenEditProduct = useCallback((policyProduct: InsurancePolicyProduct) => {
    setProductFormModalState({
      mode: 'edit',
      insuranceProductId: policyProduct.insuranceProductId,
    })
  }, [])

  // Guarded on `isSaving`, which covers every dismissal path at once — Cancel, the close
  // button, and a backdrop click. Without it an admin could dismiss a saving form, open
  // another row, and have the first request's success handler close the SECOND dialog and
  // discard its unsaved input.
  const handleCloseProductFormModal = useCallback(() => {
    if (isSaving) {
      return
    }

    setProductFormModalState(null)
  }, [isSaving])

  const handleOpenDeleteProduct = useCallback((policyProduct: InsurancePolicyProduct) => {
    setProductPendingDelete(policyProduct)
  }, [])

  const handleCloseDeleteProductModal = useCallback(() => {
    if (isDeletingPendingProduct) {
      return
    }

    setProductPendingDelete(null)
  }, [isDeletingPendingProduct])

  const handleSubmitProduct = useCallback(
    async (values: InsurancePolicyProductFormValues) => {
      if (!productFormModalState || !resolvedPolicy) {
        return
      }

      if (productFormModalState.mode === 'edit') {
        if (!productFormModalState.insuranceProductId) {
          return
        }

        const updated = await asyncDispatchWithErrorToast<InsurancePolicyProduct>(
          AdminUpdateInsurancePolicyProduct.request(
            toUpdateInsurancePolicyProductPayload(
              values,
              resolvedPolicy.permanentId,
              productFormModalState.insuranceProductId,
            ),
          ),
        )

        if (!updated) {
          return
        }

        snackbar?.enqueueSnackbar('Policy product updated', { variant: 'success' })
        setProductFormModalState(null)
        handleRefresh()

        return
      }

      const created = await asyncDispatchWithErrorToast<InsurancePolicyProduct>(
        AdminCreateInsurancePolicyProduct.request(
          toCreateInsurancePolicyProductPayload(values, resolvedPolicy.permanentId),
        ),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Product added to the policy', { variant: 'success' })
      setProductFormModalState(null)
      handleRefresh()
    },
    [productFormModalState, resolvedPolicy, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleConfirmDeleteProduct = useCallback(async () => {
    if (!productPendingDelete || !resolvedPolicy) {
      return
    }

    const deleted = await asyncDispatchWithErrorToast<string>(
      AdminDeleteInsurancePolicyProduct.request({
        insurancePolicyPermanentId: resolvedPolicy.permanentId,
        insuranceProductId: productPendingDelete.insuranceProductId,
      }),
    )

    if (!deleted) {
      return
    }

    snackbar?.enqueueSnackbar('Product removed from the policy', { variant: 'success' })
    setProductPendingDelete(null)
    handleRefresh()
  }, [productPendingDelete, resolvedPolicy, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  // "Settled for THIS customer", not "a request is in flight": gating on `loading` would blank
  // the page on every refresh and on each post-mutation refetch.
  const havePoliciesSettled = loadedPartnerId === customerId

  const insuranceTabPath = customerId
    ? buildCustomerTabPath(customerId, INSURANCE_TAB_SLUG)
    : undefined

  const errorBanner = policiesError ? (
    <div
      data-testid="admin-customer-insurance-policy-error"
      css={tw`mb-4 flex flex-row items-center gap-3 rounded-lg border border-solid border-shades-100 bg-shades-50 px-4 py-3`}
    >
      <Typography variant="body2" css={tw`text-shades-700`}>
        {/* The separator is not decoration: backend messages carry no terminal punctuation, so
            without it this reads as one run-on sentence. */}
        {`Could not load this customer's insurance policies: ${policiesError}`}
      </Typography>
      <div css={tw`ml-auto`}>
        <Button
          size="tiny"
          color="white"
          onClick={handleRefresh}
          data-testid="admin-customer-insurance-policy-retry-button"
        >
          Retry
        </Button>
      </div>
    </div>
  ) : null

  return (
    <Fragment>
      <div data-testid="admin-customer-insurance-policy-page" css={tw`min-w-0 flex-1 p-6`}>
        {errorBanner}
        {!havePoliciesSettled ? (
          <SectionCardSkeleton fields={2} dataTestId="admin-customer-insurance-policy-loading" />
        ) : null}
        {havePoliciesSettled && !resolvedPolicy ? (
          <div data-testid="admin-customer-insurance-policy-not-found">
            <Typography variant="body2" css={tw`text-shades-500`}>
              This insurance policy could not be found. It may have been deleted, or the link may be
              out of date.
            </Typography>
            {insuranceTabPath ? (
              <Link to={insuranceTabPath} data-testid="admin-customer-insurance-policy-back-link">
                Back to Insurance policies
              </Link>
            ) : null}
          </div>
        ) : null}
        {havePoliciesSettled && resolvedPolicy ? (
          <Fragment>
            <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
              <div>
                <Typography variant="h2">{resolvedPolicy.name}</Typography>
                <Typography variant="body2" css={tw`text-shades-500`}>
                  {resolvedPolicy.permanentId}
                </Typography>
              </div>
              {canManagePolicies && canViewInsuranceProducts ? (
                <Button
                  size="small"
                  color="dark"
                  // Every catalog product is already assigned, so the picker would be empty and
                  // the POST would 409. Disabled rather than hidden, so the control does not
                  // vanish.
                  disabled={selectableProducts.length === 0}
                  leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
                  onClick={handleOpenAddProduct}
                  data-testid="insurance-policy-products-list-add-button"
                >
                  Add product
                </Button>
              ) : null}
            </div>
            <InsurancePolicyProductsTable
              policyProducts={resolvedPolicy.insurancePoliciesProducts}
              canManage={canManagePolicies}
              onEditPolicyProduct={handleOpenEditProduct}
              onDeletePolicyProduct={handleOpenDeleteProduct}
            />
          </Fragment>
        ) : null}
      </div>
      <InsurancePolicyProductFormModal
        isOpen={Boolean(productFormModalState)}
        mode={productFormModalState?.mode ?? 'create'}
        policyProduct={editingPolicyProduct}
        selectableProducts={selectableProducts}
        docusignTemplates={docusignTemplates}
        isSaving={isSaving}
        onSubmit={handleSubmitProduct}
        onClose={handleCloseProductFormModal}
      />
      <DeleteInsurancePolicyProductModal
        isOpen={Boolean(productPendingDelete)}
        policyProduct={productPendingDelete}
        isDeleting={isDeletingPendingProduct}
        onConfirm={handleConfirmDeleteProduct}
        onClose={handleCloseDeleteProductModal}
      />
    </Fragment>
  )
}

export const CustomerInsurancePolicyPage = memo(CustomerInsurancePolicyPageComponent)
